/* eslint-disable no-shadow */
import { CURRENCY_SYMBOLS } from 'Constants/commons';
import { fetchSettings } from 'Helpers/utils';
import { CurrencyField, TextAreaField } from 'Modules/Formik';
import {
  createPrescription,
  getDoctorInfo,
  getOtterClinicalTerm,
  getPrescriptionConfig,
  getTemplates,
} from 'Redux/modules/webChat/prescription/actions';
import { Field, Formik } from 'formik';
import { func, number, shape } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Form } from 'semantic-ui-react';
import {
  PHARMACY_SOURCE_MANADR,
  PHARMACY_SOURCE_OTTER,
  PHARMACY_SOURCE_LIFEPACK,
} from 'Constants/consults';

import PrescriptionPreview from '../PrescriptionPreview/PrescriptionPreview';

import AdvancedFieldArray from './AdvancedFieldArray/AdvancedFieldArray';
import BasicFieldArray from './BasicFieldArray/BasicFieldArray';
import styles from './PrescriptionForm.less';
import { PrescriptionMaNaPharmaSchema, PrescriptionSchema } from './validate.js';
import { formatGlocoMedicine, formatLifepackMedicine, formatOtterSgMedicine } from './helpers';

const getSignatureUrl = signatureFileId =>
  `${fetchSettings().FILE_API_URL}/files/${signatureFileId}`;

function initialValues(deliveryFee, prescription = null, serviceId, practiceCost) {
  const medicines =
    prescription?.pharmacy?.hasStoredMedicines ||
    [PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK].includes(prescription?.pharmacy?.source)
      ? prescription?.medicines.map(medicine => ({
          ...medicine,
          uomUnit: medicine?.uom ?? '',
          doseUnit: medicine?.doseUnit ?? '',
          instructionId: '',
          description: {
            id: medicine.description,
            name: medicine.description,
            quantity: medicine.quantity,
            unitPrice: medicine.unitPrice,
            unitPriceBeforeTax: medicine.unitPriceBeforeTax,
            vendor: '',
          },
        }))
      : prescription?.medicines ?? [];
  return {
    medicines,
    note: prescription?.note ?? '',
    deliveryFee: prescription?.deliveryFee ?? deliveryFee ?? 0,
    isPrepayment: 'true',
    serviceId,
    practiceCost,
  };
}

class PrescriptionForm extends Component {
  componentDidMount() {
    const {
      doctorId,
      getDoctorInfo,
      getTemplates,
      selectedPharmacy: { hasStoredMedicines },
      getPrescriptionConfig,
      getOtterClinicalTerm,
      selectedPharmacy,
    } = this.props;

    getDoctorInfo(doctorId);
    getPrescriptionConfig();
    if (hasStoredMedicines) {
      getTemplates();
    }

    if (selectedPharmacy.source === PHARMACY_SOURCE_OTTER) {
      getOtterClinicalTerm({ tenantId: selectedPharmacy.tenantId });
    } else if (selectedPharmacy.source === PHARMACY_SOURCE_LIFEPACK) {
      getOtterClinicalTerm();
    }
  }

  issuePrescription = async values => {
    const {
      createPrescription,
      conversation: {
        latestConsult: {
          id: consultId,
          forPatient: { id: forPatientId },
          patient: { id: patientId },
        },
      },
      selectedPharmacy: { id: pharmacyId, hasStoredMedicines, currency, source },
      profile: { signatureFileId },
      close,
    } = this.props;

    const { medicines, note, deliveryFee, isPrepayment } = values;
    let formattedMedicines = [];
    if (source === PHARMACY_SOURCE_OTTER) {
      formattedMedicines = medicines.map(medicine => ({
        ...formatOtterSgMedicine(medicine),
        isTakenFromStore: hasStoredMedicines,
      }));
    } else if (source === PHARMACY_SOURCE_LIFEPACK) {
      formattedMedicines = medicines.map(medicine => ({
        ...formatLifepackMedicine(medicine),
        isTakenFromStore: hasStoredMedicines,
      }));
    } else if (hasStoredMedicines) {
      // Handle for Gloco
      formattedMedicines = medicines.map(medicine => ({
        ...formatGlocoMedicine(medicine),
        isTakenFromStore: hasStoredMedicines,
      }));
    } else {
      formattedMedicines = medicines.map(medicine => ({
        description: medicine.description,
        unitPrice: +medicine.unitPrice,
        quantity: +medicine.quantity,
      }));
    }
    const data = {
      consultId,
      patientId,
      forPatientId,
      note,
      signFileId: signatureFileId,
      pharmacyId,
      medicines: formattedMedicines,
      currency,
      deliveryFee: +deliveryFee,
      isPrepayment: isPrepayment === 'true',
      source: [PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK].includes(source)
        ? source
        : PHARMACY_SOURCE_MANADR,
      serviceId: values.serviceId,
    };

    await createPrescription(data);
    close();
  };

  onSubmit = values => {
    this.issuePrescription(values);
  };

  render() {
    const {
      bindSubmitForm,
      selectedPharmacy,
      conversation,
      conversation: { latestConsult },
      prescription: { doctorInfo, templates, deliveryFee },
      profile: { signatureFileId },
      pendingPrescription,
    } = this.props;

    const signatureUrl = getSignatureUrl(signatureFileId);
    const currencySymbol = CURRENCY_SYMBOLS[selectedPharmacy?.currency] || '';
    const serviceId = latestConsult?.service?.id;
    const practiceCost = latestConsult?.practiceCost;
    const pharmacySource = selectedPharmacy.source;

    const canSelectMedicineFromStorage =
      selectedPharmacy.hasStoredMedicines ||
      [PHARMACY_SOURCE_OTTER, PHARMACY_SOURCE_LIFEPACK].includes(pharmacySource);

    return (
      <Formik
        initialValues={initialValues(
          selectedPharmacy?.deliveryFee?.amount,
          pendingPrescription,
          serviceId,
          practiceCost,
        )}
        validationSchema={
          canSelectMedicineFromStorage ? PrescriptionMaNaPharmaSchema : PrescriptionSchema
        }
        onSubmit={this.onSubmit}>
        {({ handleSubmit, values, submitForm }) => {
          bindSubmitForm(submitForm);
          return (
            <div className={styles.prescription}>
              <Form onSubmit={handleSubmit} className={styles.prescriptionForm}>
                <h3 style={{ marginBottom: 20 }}>Issue Prescription</h3>
                {canSelectMedicineFromStorage ? (
                  <AdvancedFieldArray
                    values={values}
                    currencySymbol={currencySymbol}
                    templates={templates}
                    selectedPharmacy={selectedPharmacy}
                    serviceId={serviceId}
                  />
                ) : (
                  <BasicFieldArray values={values} currencyCode={currencySymbol} />
                )}

                {!!values.practiceCost && (
                  <Field
                    label="Practice Cost"
                    name="practiceCost"
                    component={CurrencyField}
                    currencyCode={currencySymbol}
                    excludeSeparator
                    disabled
                  />
                )}

                {pharmacySource !== PHARMACY_SOURCE_LIFEPACK && (
                  // Always hide for Lifepack
                  <Field
                    label="Shipping Fee"
                    name="deliveryFee"
                    component={CurrencyField}
                    currencyCode={currencySymbol}
                    excludeSeparator
                  />
                )}

                <Field
                  label={<span className={styles.labelImportant}>Important Note</span>}
                  name="note"
                  maxlengthchar={500}
                  component={TextAreaField}
                  placeholder="Note for patient ..."
                />
              </Form>
              <div className={styles.prescriptionPreview}>
                <h3>Preview</h3>
                <PrescriptionPreview
                  values={values}
                  pharmacy={selectedPharmacy}
                  conversation={conversation}
                  signatureUrl={signatureUrl}
                  currencyCode={currencySymbol}
                  doctorInfo={doctorInfo}
                  deliveryFee={deliveryFee}
                  practiceCost={practiceCost}
                />
              </div>
            </div>
          );
        }}
      </Formik>
    );
  }
}

PrescriptionForm.propTypes = {
  createPrescription: func.isRequired,
  conversation: shape({}).isRequired,
  prescription: shape({}),
  pendingPrescription: shape({}),
  close: func.isRequired,
  bindSubmitForm: func.isRequired,
  profile: shape({}).isRequired,
  selectedPharmacy: shape({}).isRequired,
  doctorId: number.isRequired,
  getDoctorInfo: func.isRequired,
  getTemplates: func.isRequired,
  getPrescriptionConfig: func.isRequired,
  getOtterClinicalTerm: func.isRequired,
};

PrescriptionForm.defaultProps = {
  prescription: {},
  pendingPrescription: null,
};

const mapStateToProps = state => ({
  files: state.files,
  doctorId: state.auth.data.accountId,
  doctor: state.webChat.doctor.data,
  prescription: state.webChat.prescription,
  profile: state.doctors.profile,
});

const mapDispatchToProps = {
  createPrescription,
  getDoctorInfo,
  getTemplates,
  getPrescriptionConfig,
  getOtterClinicalTerm,
};

export default connect(mapStateToProps, mapDispatchToProps)(PrescriptionForm);
